import { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../../contexts/AuthContext';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const fmt = (d) => d ? String(d).slice(0, 10) : '';

const th = { padding: '0.5rem 0.75rem', background: 'linear-gradient(135deg,#D84315,#F57C00)', color: '#fff', fontWeight: 600, fontSize: '0.8rem', textAlign: 'left', whiteSpace: 'nowrap' };
const td = { padding: '0.45rem 0.75rem', fontSize: '0.875rem', borderBottom: '1px solid #e0e0e0', color: '#2c3e50', textAlign: 'center' };
const tdl = { ...td, textAlign: 'left' };
const inp = { padding: '0.4rem 0.6rem', border: '1px solid #ccc', borderRadius: 5, fontSize: '0.875rem' };
const btn = (variant = 'primary', disabled = false) => ({
  padding: '0.4rem 0.9rem', border: 'none', borderRadius: 5, fontWeight: 600, fontSize: '0.8rem',
  cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.5 : 1,
  background: variant === 'primary' ? 'linear-gradient(135deg,#D84315,#F57C00)' : variant === 'secondary' ? '#e0e0e0' : variant === 'success' ? '#27ae60' : '#e74c3c',
  color: variant === 'secondary' ? '#333' : '#fff',
});

const STATES = ['Entered', 'Fulfilled', 'Payment Initiated', 'Paid'];

export default function PayExpenses() {
  const { user } = useContext(AuthContext);
  const headers = { Authorization: `Bearer ${user?.token}` };
  const subsidiary = localStorage.getItem('selectedSubsidiaryKey') || '';
  const navigate = useNavigate();

  const [expenses, setExpenses]         = useState([]);
  const [itemNames, setItemNames]       = useState([]);
  const [employees, setEmployees]       = useState([]);
  const [paymentTypes, setPaymentTypes] = useState([]);
  const [paymentLOV, setPaymentLOV]     = useState([]);

  const [selectedRows, setSelectedRows] = useState([]);
  const [selectAll, setSelectAll]       = useState(false);

  const [showModal, setShowModal]       = useState(false);
  const [payState, setPayState]         = useState('Entered');
  const [paymentTypeId, setPaymentTypeId] = useState('');
  const [paymentId, setPaymentId]       = useState('');
  const [paymentDate, setPaymentDate]   = useState('');
  const [balanceAmount, setBalanceAmount] = useState('');

  const [search, setSearch] = useState({
    name: '', expense_date: '', expense_no: '', expense_item_id: '',
    expense_state: 'Entered', expense_paid: '', subsidiary, vendor_id: '', created_by_id: '',
  });

  useEffect(() => {
    axios.get(`${API_URL}/commonLOVs/lhexpenseitems/${subsidiary || 'LH'}`, { headers }).then(r => setItemNames(r.data || [])).catch(() => {});
    axios.get(`${API_URL}/commonLOVs/lhusers`, { headers }).then(r => setEmployees(r.data || [])).catch(() => {});
    axios.get(`${API_URL}/commonLOVs/payment-types`, { headers }).then(r => setPaymentTypes(r.data || [])).catch(() => {});
    axios.get(`${API_URL}/commonLOVs/lhpayments`, { headers }).then(r => setPaymentLOV(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (payState === 'Paid' && paymentTypeId === 1 && paymentId) {
      axios.get(`${API_URL}/payments/byPaymentId/${paymentId}`, { headers })
        .then(r => { setPaymentDate(fmt(r.data.payment_date)); setBalanceAmount(r.data.balance_amount ?? ''); })
        .catch(() => {});
    } else {
      setPaymentId(''); setPaymentDate(''); setBalanceAmount('');
    }
  }, [paymentId, payState, paymentTypeId]);

  const fetchExpenses = async (s = search) => {
    try {
      const res = await axios.get(`${API_URL}/expenses?${new URLSearchParams(s)}`, { headers });
      setExpenses(res.data || []);
      setSelectedRows([]); setSelectAll(false);
    } catch { setExpenses([]); }
  };

  const reset = () => {
    const r = { name: '', expense_date: '', expense_no: '', expense_item_id: '', expense_state: 'Entered', expense_paid: '', subsidiary, vendor_id: '', created_by_id: '' };
    setSearch(r); setSelectedRows([]); setSelectAll(false);
    fetchExpenses(r);
  };

  const toggleRow = (id) => setSelectedRows(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);
  const toggleAll = () => {
    if (selectAll) { setSelectedRows([]); } else { setSelectedRows(expenses.filter(e => e.expense_state !== 'Paid').map(e => e.id)); }
    setSelectAll(p => !p);
  };

  const openModal = () => {
    setPayState(expenses.find(e => selectedRows.includes(e.id))?.expense_state || 'Entered');
    setPaymentTypeId(''); setPaymentId(''); setShowModal(true);
  };

  const savePayment = async () => {
    try {
      await axios.post(`${API_URL}/expenses/bulkStateUpdate`, {
        expense_ids: selectedRows,
        expense_state: payState,
        payment_type_id: paymentTypeId,
        payment_id: payState === 'Paid' && paymentTypeId === 1 ? paymentId : null,
      }, { headers });
      setShowModal(false);
      fetchExpenses();
    } catch { alert('Failed to update expenses.'); }
  };

  const totalSelected = expenses.filter(e => selectedRows.includes(e.id)).reduce((s, e) => s + parseFloat(e.expense_amount || 0), 0);

  const saveDisabled = !paymentTypeId || (paymentTypeId === 1 && (!paymentId || (payState === 'Paid' && parseFloat(balanceAmount) !== totalSelected)));

  return (
    <div style={{ padding: '1.25rem' }}>
      <button style={{ ...btn('secondary'), marginBottom: '1rem' }} onClick={() => navigate(-1)}>← Back</button>
      <h2 style={{ margin: '0 0 1rem', fontSize: '1.2rem', color: '#2c3e50' }}>Pay Expenses</h2>

      {/* Search */}
      <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: 8, padding: '1rem', marginBottom: '1rem', display: 'flex', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'flex-end' }}>
        <input style={{ ...inp, width: 120 }} placeholder="Expense No" value={search.expense_no} onChange={e => setSearch(p => ({ ...p, expense_no: e.target.value }))} />
        <input style={{ ...inp, width: 140 }} placeholder="Name" value={search.name} onChange={e => setSearch(p => ({ ...p, name: e.target.value }))} />
        <input type="date" style={{ ...inp, width: 140 }} value={search.expense_date} onChange={e => setSearch(p => ({ ...p, expense_date: e.target.value }))} />
        <select style={{ ...inp, width: 130 }} value={search.created_by_id} onChange={e => setSearch(p => ({ ...p, created_by_id: e.target.value }))}>
          <option value="">Employee</option>
          {employees.map(em => <option key={em.id} value={em.id}>{em.name}</option>)}
        </select>
        <select style={{ ...inp, width: 130 }} value={search.expense_item_id} onChange={e => setSearch(p => ({ ...p, expense_item_id: e.target.value }))}>
          <option value="">Item</option>
          {itemNames.map(i => <option key={i.id} value={i.id}>{i.name}</option>)}
        </select>
        <select style={{ ...inp, width: 150 }} value={search.expense_state} onChange={e => setSearch(p => ({ ...p, expense_state: e.target.value }))}>
          {STATES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <button style={btn()} onClick={() => fetchExpenses(search)}>Search</button>
        <button style={btn('secondary')} onClick={reset}>Reset</button>
        <button style={btn('success', selectedRows.length === 0)} disabled={selectedRows.length === 0} onClick={openModal}>Pay</button>
      </div>

      {/* Table */}
      <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: 8, overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={{ ...th, textAlign: 'center' }}><input type="checkbox" checked={selectAll} onChange={toggleAll} /></th>
              <th style={th}>Expense No</th>
              <th style={th}>Name</th>
              <th style={th}>Item</th>
              <th style={th}>Date</th>
              <th style={th}>Amount</th>
              <th style={th}>State</th>
              <th style={th}>Paid</th>
            </tr>
          </thead>
          <tbody>
            {expenses.length === 0 ? (
              <tr><td colSpan={8} style={{ ...tdl, textAlign: 'center', color: '#999', padding: '1.5rem' }}>No expenses found. Search to load.</td></tr>
            ) : expenses.map(e => (
              <tr key={e.id}>
                <td style={td}><input type="checkbox" checked={selectedRows.includes(e.id)} onChange={() => toggleRow(e.id)} disabled={e.expense_state === 'Paid'} /></td>
                <td style={td}>{e.expense_no}</td>
                <td style={tdl}>{e.name}</td>
                <td style={tdl}>{e.item_name}</td>
                <td style={td}>{fmt(e.expense_date)}</td>
                <td style={td}>{e.expense_amount}</td>
                <td style={td}>{e.expense_state}</td>
                <td style={td}>{e.expense_paid === 'Y' ? 'Yes' : 'No'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      {showModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
          <div style={{ background: '#fff', borderRadius: 10, width: 420, padding: '1.5rem', boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}>
            <h3 style={{ margin: '0 0 1rem', fontSize: '1rem', fontWeight: 600 }}>Confirm Payment</h3>
            <p style={{ marginBottom: '1rem', fontWeight: 600, fontSize: '0.9rem' }}>Total: ₹{totalSelected.toFixed(2)}</p>

            <div style={{ marginBottom: '0.75rem' }}>
              <label style={{ fontSize: '0.8rem', fontWeight: 600, display: 'block', marginBottom: 3 }}>Expense State</label>
              <select style={{ ...inp, width: '100%' }} value={payState} onChange={e => setPayState(e.target.value)}>
                {STATES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>

            <div style={{ marginBottom: '0.75rem' }}>
              <label style={{ fontSize: '0.8rem', fontWeight: 600, display: 'block', marginBottom: 3 }}>Payment Type</label>
              <select style={{ ...inp, width: '100%' }} value={paymentTypeId} onChange={e => setPaymentTypeId(parseInt(e.target.value))}>
                <option value="">Select Payment Type</option>
                {paymentTypes.map(pt => <option key={pt.id} value={pt.id}>{pt.name}</option>)}
              </select>
            </div>

            {payState === 'Paid' && paymentTypeId === 1 && (
              <div style={{ marginBottom: '0.75rem' }}>
                <label style={{ fontSize: '0.8rem', fontWeight: 600, display: 'block', marginBottom: 3 }}>Payment</label>
                <select style={{ ...inp, width: '100%' }} value={paymentId} onChange={e => setPaymentId(e.target.value)}>
                  <option value="">Select Payment</option>
                  {paymentLOV.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
            )}

            {payState === 'Paid' && paymentTypeId === 1 && paymentId && (
              <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '0.75rem' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: '0.8rem', fontWeight: 600, display: 'block', marginBottom: 3 }}>Payment Date</label>
                  <input style={{ ...inp, width: '100%', background: '#f5f5f5' }} value={paymentDate} readOnly />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: '0.8rem', fontWeight: 600, display: 'block', marginBottom: 3 }}>Balance Amount</label>
                  <input style={{ ...inp, width: '100%', background: '#f5f5f5' }} value={balanceAmount} readOnly />
                </div>
              </div>
            )}

            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
              <button style={btn('secondary')} onClick={() => setShowModal(false)}>Cancel</button>
              <button style={btn('success', saveDisabled)} disabled={saveDisabled} onClick={savePayment}>Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
